'use client'

import {
  DefaultNodeTypes,
  SerializedBlockNode,
  SerializedHeadingNode,
  SerializedLinkNode,
  type DefaultTypedEditorState,
} from '@payloadcms/richtext-lexical'
import {
  JSXConvertersFunction,
  LinkJSXConverter,
  RichText as ConvertRichText,
} from '@payloadcms/richtext-lexical/react'
import { useTranslations } from 'next-intl'

import type {
  BannerBlock as BannerBlockProps,
  CallToActionBlock as CTABlockProps,
  MediaBlock as MediaBlockProps,
} from '@/payload-types'
import {
  createRichTextBlockConvertersClient,
  type RichTextBlockConverterOptions,
} from '@/components/RichText/blockConverters.client'
import type { CodeBlockProps } from '@/blocks/Code/Component'
import type { TocEntry, TocHeadingLevel } from '@/utilities/extractTocFromLexical'
import { TOC_HEADER_OFFSET_PX } from '@/utilities/tocConstants'
import { cn } from '@/utilities/ui'
import { buildPostUrl, type PostUrlStructure } from '@/utilities/resolvePostPattern'
import { getPostUrlPrefix } from '@/utilities/getPostUrlPrefix'
import { hrefFromPageDoc } from '@/utilities/hrefFromPageDoc'

type NodeTypes =
  | DefaultNodeTypes
  | SerializedBlockNode<CTABlockProps | MediaBlockProps | BannerBlockProps | CodeBlockProps>

function makeInternalDocToHref(urlPrefix: string, urlStructure?: PostUrlStructure | string) {
  return ({ linkNode }: { linkNode: SerializedLinkNode }): string => {
    const { value, relationTo } = linkNode.fields.doc!
    if (typeof value !== 'object') {
      throw new Error('Expected value to be an object')
    }
    const slug = value.slug as string
    if (relationTo === 'posts') {
      return buildPostUrl(urlStructure as PostUrlStructure | null, slug, urlPrefix, null)
    }
    return hrefFromPageDoc(value as { slug?: string | null; fullPath?: string | null })
  }
}

function makeParagraphConverter(markFirstBodyParagraphAbstract?: boolean) {
  let firstContentParagraphDone = false
  return function paragraphConverter({
    node,
    nodesToJSX,
  }: {
    node: DefaultNodeTypes & { type: 'paragraph' }
    nodesToJSX: (args: { nodes: typeof node.children }) => React.ReactNode
  }): React.ReactNode {
    const children = (node as any).children as Array<{ type: string }>
    const hasContent = Array.isArray(children) && children.some((c) => c.type !== 'linebreak')
    if (!hasContent) return null

    const markAbstract =
      Boolean(markFirstBodyParagraphAbstract) && !firstContentParagraphDone
    if (markAbstract) firstContentParagraphDone = true

    const jsxChildren = nodesToJSX({ nodes: (node as any).children })
    const fmt: string = (node as any).format ?? ''
    const needsStyle = fmt !== '' && fmt !== 'left' && fmt !== 'start'
    const style = needsStyle
      ? { textAlign: fmt as React.CSSProperties['textAlign'] }
      : undefined

    return (
      <p style={style} {...(markAbstract ? ({ 'data-abstract': '' } as const) : {})}>
        {jsxChildren}
      </p>
    )
  }
}

function makeJsxConverters(
  urlPrefix: string,
  urlStructure?: PostUrlStructure | string,
  blockOptions?: RichTextBlockConverterOptions,
  markFirstBodyParagraphAbstract?: boolean,
): JSXConvertersFunction<NodeTypes> {
  const internalDocToHref = makeInternalDocToHref(urlPrefix, urlStructure)
  return ({ defaultConverters }) => ({
    ...defaultConverters,
    ...LinkJSXConverter({ internalDocToHref }),
    blocks: {
      ...defaultConverters.blocks,
      ...createRichTextBlockConvertersClient(blockOptions),
    },
    paragraph: makeParagraphConverter(markFirstBodyParagraphAbstract) as any,
    quote: ({ node, nodesToJSX }) => (
      <blockquote className="post-rich-quote">{nodesToJSX({ nodes: node.children })}</blockquote>
    ),
  })
}

function createRichTextConvertersWithToc(
  tocEntries: readonly TocEntry[],
  urlPrefix: string,
  urlStructure?: PostUrlStructure | string,
  blockOptions?: RichTextBlockConverterOptions,
  markFirstBodyParagraphAbstract?: boolean,
): JSXConvertersFunction<NodeTypes> {
  const internalDocToHref = makeInternalDocToHref(urlPrefix, urlStructure)
  return ({ defaultConverters }) => {
    const indexByTag: Record<TocHeadingLevel, number> = { h2: 0, h3: 0, h4: 0 }

    return {
      ...defaultConverters,
      ...LinkJSXConverter({ internalDocToHref }),
      blocks: {
        ...defaultConverters.blocks,
        ...createRichTextBlockConvertersClient(blockOptions),
      },
      paragraph: makeParagraphConverter(markFirstBodyParagraphAbstract) as any,
      quote: ({ node, nodesToJSX }) => (
        <blockquote className="post-rich-quote">{nodesToJSX({ nodes: node.children })}</blockquote>
      ),
      heading: ({ node, nodesToJSX }) => {
        const children = nodesToJSX({ nodes: node.children })
        const n = node as SerializedHeadingNode
        const Tag = n.tag

        if (Tag !== 'h2' && Tag !== 'h3' && Tag !== 'h4') {
          return <Tag>{children}</Tag>
        }

        const tag = Tag as TocHeadingLevel
        const sameLevel = tocEntries.filter((e) => e.tag === tag)
        const i = indexByTag[tag]++
        const id = sameLevel[i]?.id

        if (!id) return <Tag>{children}</Tag>

        return (
          <Tag id={id} style={{ scrollMarginTop: TOC_HEADER_OFFSET_PX }}>
            {children}
          </Tag>
        )
      },
    }
  }
}

type Props = {
  data: DefaultTypedEditorState
  enableGutter?: boolean
  enableProse?: boolean
  tocEntries?: readonly TocEntry[]
  urlPrefix?: string
  urlStructure?: PostUrlStructure | string
  postTitle?: string
  mediaNearestHeadings?: readonly string[]
  markFirstBodyParagraphAbstract?: boolean
} & React.HTMLAttributes<HTMLDivElement>

/**
 * Rich text no **client** (ex.: intro do Form Builder): blocos que dependem do servidor viram placeholders
 * para não importar `payload.config` / Node no bundle do browser.
 */
export default function RichTextForm(props: Props) {
  const {
    className,
    enableProse = true,
    enableGutter = true,
    tocEntries,
    data,
    urlPrefix,
    urlStructure,
    postTitle,
    mediaNearestHeadings,
    markFirstBodyParagraphAbstract,
    ...rest
  } = props

  const resolvedPrefix = urlPrefix ?? getPostUrlPrefix()

  const tEmbed = useTranslations('Embed')
  const embedStrings = {
    iframeTitle: tEmbed('youtubeIframeTitle'),
    fallbackAriaLabel: tEmbed('youtubeFallbackAriaLabel'),
  }

  const tBlocks = useTranslations('Blocks')
  const blockStrings = {
    bannerInfo: tBlocks('bannerInfo'),
    bannerError: tBlocks('bannerError'),
    bannerSuccess: tBlocks('bannerSuccess'),
    bannerWarning: tBlocks('bannerWarning'),
    bannerDefault: tBlocks('bannerDefault'),
    faqTitle: tBlocks('faqTitle'),
    definitionLabel: tBlocks('definitionLabel'),
    definitionAria: tBlocks('definitionAria'),
    mediaStaticFallbackAlt: tBlocks('mediaStaticFallbackAlt'),
  }

  const blockOptions: RichTextBlockConverterOptions = {
    embedStrings,
    blockStrings,
    ...(postTitle?.trim() ? { postTitle: postTitle.trim() } : {}),
    ...(mediaNearestHeadings && mediaNearestHeadings.length > 0 ? { mediaNearestHeadings } : {}),
  }

  const converters =
    tocEntries && tocEntries.length > 0
      ? createRichTextConvertersWithToc(
          tocEntries,
          resolvedPrefix,
          urlStructure,
          blockOptions,
          markFirstBodyParagraphAbstract,
        )
      : makeJsxConverters(resolvedPrefix, urlStructure, blockOptions, markFirstBodyParagraphAbstract)

  return (
    <ConvertRichText
      converters={converters}
      data={data}
      className={cn(
        'payload-richtext',
        {
          container: enableGutter,
          'max-w-none': !enableGutter,
          'mx-auto prose md:prose-md dark:prose-invert': enableProse,
        },
        className,
      )}
      {...rest}
    />
  )
}
